import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import type { RuleExecution } from '../types';

const History: React.FC = () => {
  const [executions, setExecutions] = useState<RuleExecution[]>([]);
  const [days, setDays] = useState<number>(30);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch rule execution history
  const fetchHistory = async (daysParam: number) => {
    try {
      setLoading(true);
      const since = new Date(Date.now() - daysParam * 24 * 60 * 60 * 1000);
      const response = await apiService.getRuleExecutions({ start_date: since.toISOString() });
      if (response.success) {
        setExecutions(response.data.executions || []);
        setError(null);
      } else {
        setError(response.error?.message || 'Failed to load history');
      }
    } catch (err) {
      console.error('Error fetching rule execution history:', err);
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const handleDaysChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDays = parseInt(e.target.value, 10);
    setDays(newDays);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
          <p className="text-slate-600 font-medium">Loading execution history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">Rule Execution History</h1>
              <p className="text-slate-600 text-lg">Track when your portfolio rules were triggered and their outcomes.</p>
            </div>
            
            {/* Time Period Filter */}
            <div className="flex items-center space-x-3">
              <label className="text-sm font-medium text-slate-700">Time Period:</label>
              <div className="relative">
                <select 
                  value={days} 
                  onChange={handleDaysChange} 
                  className="input-modern pr-10 py-2.5 min-w-36 shadow-sm appearance-none bg-white"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
                <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="card bg-gradient-to-r from-red-50 to-rose-50 border-red-200 p-4 mb-6 animate-fade-in">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}
        
        {/* Execution History Table */}
        <div className="card table-modern animate-slide-in">
          {executions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="text-left">Symbol</th>
                    <th className="text-left">Rule Type</th>
                    <th className="text-left">Triggered At</th>
                    <th className="text-left">Status</th>
                    <th className="text-left">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {executions.map((exec) => (
                    <tr key={exec.id} className="group">
                      <td className="font-medium text-slate-900">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                            <span className="text-white text-xs font-bold">
                              {exec.symbol.charAt(0)}
                            </span>
                          </div>
                          <span>{exec.symbol}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          exec.trigger_type === 'STOP_LOSS' 
                            ? 'bg-red-100 text-red-800'
                            : exec.trigger_type === 'TAKE_PROFIT'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {exec.trigger_type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="text-slate-600">
                        <div className="flex flex-col">
                          <span className="font-medium">{exec.executed_at.split(', ')[0]}</span>
                          <span className="text-xs text-slate-500">{exec.executed_at.split(', ')[1]}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          exec.status === 'SUCCESS' 
                            ? 'bg-green-100 text-green-800'
                            : exec.status === 'FAILED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            exec.status === 'SUCCESS' 
                              ? 'bg-green-500'
                              : exec.status === 'FAILED'
                              ? 'bg-red-500'
                              : 'bg-yellow-500'
                          }`}></div>
                          {exec.status}
                        </span>
                      </td>
                      <td className="text-slate-600 max-w-xs">
                        <div className="truncate">
                          {exec.message || (
                            <span className="text-slate-400 italic">No additional details</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">No executions found</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                No rule executions were found for the selected time period. Rules will appear here when they are triggered.
              </p>
              <Link 
                to="/holdings" 
                className="btn btn-primary px-6 py-2.5 shadow-md hover:shadow-lg"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Set up Rules
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default History; 